import {ApiTags, baseApi} from "../../../app/api/baseApi";
import {CmQueryResult} from "../../../app/types";
import {ChangeResourceLocationPayload, MediabankResource, MediabankResourceWithPropID, ResourcePayload} from "../types";
import UseApiV1 from "../../../hooks/useApiV1";
import {OwnershipAttribute} from "../../creations/types";
import {MediaFormat} from "../../drafts/types";

export const mediabankResourcesApiSlice = baseApi.injectEndpoints({
    endpoints: builder => ({
        getMediabankResources: builder.query<CmQueryResult<MediabankResource[]>, string>({
            query: (query) => {
                return {
                    reducerPath: 'mediabankResources',
                    url: UseApiV1(`mediabank_resources?${query}`),
                    method: 'GET',
                }
            },
            keepUnusedDataFor: 120,
            providesTags: (_) => [ApiTags.MediabankResourcesList],
        }),
        getMediabankResourcesForSelect: builder.query<CmQueryResult<MediabankResource[]>, {parentId: string | null, mediaFormat: string | null}>({
            query: (params: {parentId: string | null, mediaFormat: MediaFormat | null}) => {
                const mediaFormatParam = params.mediaFormat ===  MediaFormat.moveTv? `q[aspect_ratio_in][]=horizontal` : ''

                return {
                    reducerPath: 'mediabankResources',
                    url: UseApiV1(params.parentId? `mediabank_resources?q[parent_id_eq]=${params.parentId}&${mediaFormatParam}` : `mediabank_resources?${mediaFormatParam}`),
                    method: 'GET',
                }
            },
            keepUnusedDataFor: 120,
            transformResponse: (response: CmQueryResult<MediabankResource[]>) => {
                return {
                    ...response,
                    data: response.data.map((el, index) => {
                        return {
                            ...el,
                            editableLength: (index % 3 === 0),
                            length: index % 3 === 0 ? 0 : Math.floor(Math.random() * 60) + 1
                        }
                    }),
                }
            },
            providesTags: (_) => [ApiTags.MediabankResourcesList],
        }),
        createResource: builder.mutation<any, ResourcePayload>({
            query: (mediabankResourcePayload: ResourcePayload): any => {
                return {
                    url: UseApiV1('mediabank_resources'),
                    method: 'POST',
                    body: {...mediabankResourcePayload}
                }
            },
            invalidatesTags: (_) => [ApiTags.MediabankResourcesList],
        }),
        changeResourceData: builder.mutation<any, any>({
            query: (changeMediabankResourcePayload:any): any => {
                return {
                    url: UseApiV1(`mediabank_resources/${changeMediabankResourcePayload.id}`),
                    method: 'PUT',
                    body: {...changeMediabankResourcePayload}
                }
            },
            invalidatesTags: (_) => [ApiTags.MediabankResourcesList],
        }),
        getMediabankResource: builder.query<CmQueryResult<MediabankResource & {ownerships: OwnershipAttribute[]}>, number>({
            query: (id) => {
                return {
                    reducerPath: 'mediabankResources',
                    url: UseApiV1(`mediabank_resources/${id}`),
                    method: 'GET',
                }
            },
            keepUnusedDataFor: 120,
        }),
        getMediabankResourcesBulk: builder.query<MediabankResourceWithPropID[], {resourceId: number, propId: number}[]>({
            queryFn: (resourcesIds: {resourceId: number, propId: number}[], queryApi) => {
                const promises: Promise<any>[] = resourcesIds.map((resourcesIds) => {
                    return queryApi.dispatch(mediabankResourcesApiSlice.endpoints.getMediabankResource.initiate(resourcesIds.resourceId)).then((data) => {
                        return {...data, propId: resourcesIds.propId}
                    });
                });
                return Promise.all(promises).then((results) => {
                    return { data: results.map((el) => {return {...el.data.data, propId: el.propId}}) };
                });
            },
        }),
        changeResourceLocation: builder.mutation<any, ChangeResourceLocationPayload[]>({
            query: (changeResourceLocationPayload: ChangeResourceLocationPayload[]): any => {
                return {
                    url: UseApiV1('mediabank_resources_bulk'),
                    method: 'PATCH',
                    body: changeResourceLocationPayload
                }
            },
            invalidatesTags: (_) => [ApiTags.MediabankResourcesList],
        }),
    })
})

export const {
    useGetMediabankResourcesQuery,
    useGetMediabankResourcesForSelectQuery,
    useCreateResourceMutation,
    useChangeResourceDataMutation,
    useLazyGetMediabankResourceQuery,
    useLazyGetMediabankResourcesBulkQuery,
    useChangeResourceLocationMutation
} = mediabankResourcesApiSlice